import { useState } from 'react';
import styled from 'styled-components';

import { MenuCheckboxItem } from '@creditornot/cb-menu';
import { Empty } from '@creditornot/cb-placeholders';
import { ApiErrorNotification } from 'common/components/cookbook/ApiErrorNotification';
import { LoadingBar } from 'common/components/cookbook/LoadingBar';
import { ModalSpinner } from 'common/components/cookbook/Modals/ModalSpinner';
import { SearchInput } from 'common/components/cookbook/Search/SearchInput';
import { Stack } from 'common/components/cookbook/Stack';
import { ThinScrollbarContainer } from 'common/components/cookbook/ThinScrollbarContainer';
import { useFormatMessage } from 'common/hooks/useFormatMessage';
import { useSelectedCountryCodes } from 'common/hooks/useSelectedCountryCodes';
import { useVendorsQuery } from 'features/vendors/query';

const ItemList = styled(ThinScrollbarContainer)`
  width: 320px;
  max-height: 400px;
  display: flex;
  flex-direction: column;
  gap: 8px;

  & > * {
    flex-shrink: 0;
  }
`;

type Props = {
  onVendorChange: (vendor: string) => void;
  selectedVendorIds: string[];
};

export const VendorFilter = ({ onVendorChange, selectedVendorIds }: Props) => {
  const countryCodes = useSelectedCountryCodes();
  const f = useFormatMessage();
  const [searchQuery, setSearchQuery] = useState('');

  const {
    data: vendorsData,
    isLoading,
    isRefetching,
    error,
  } = useVendorsQuery({
    countryCodes,
    q: searchQuery,
  });

  const vendors = vendorsData?.items ?? [];

  return (
    <Stack gap="regularMedium">
      <SearchInput value={searchQuery} onChange={(value) => setSearchQuery(value)} autoFocus />
      {isLoading && <ModalSpinner />}
      {isRefetching && <LoadingBar />}
      <ApiErrorNotification error={error} />
      {vendors && !isLoading && (
        <ItemList role="menu">
          {vendors.map((vendor) => (
            <MenuCheckboxItem
              key={vendor.id}
              checked={selectedVendorIds.includes(vendor.id)}
              onClick={() => onVendorChange(vendor.id)}
              value={vendor.id}
            >
              {vendor.name}
            </MenuCheckboxItem>
          ))}
          {vendors.length === 0 && <Empty description={f('common.no_vendors_found')} />}
        </ItemList>
      )}
    </Stack>
  );
};
